import { useState } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import Layout from "@/components/elements/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { getStartupEventById, deleteStartupEvent, publishStartupEvent, unPublishStartupEvent, approveStartupEvent } from '@/services/api/StartupEvent';
import { format } from 'date-fns';
import Image from 'next/image';
import { Calendar, MapPin, Globe, User, Pencil } from '@phosphor-icons/react';
import ModalConfirm from '@/components/ModalConfirm';
import useAuth from '@/hooks/auth';
import toast from 'react-hot-toast';
import NotFoundComponent from '@/components/NotFoundComponent';
import DummyLogo from "@/assets/images/lucidity-placeholder.png";

// Dynamically import the Map component with ssr: false
const Map = dynamic(() => import('@/components/Map'), { ssr: false });

export default function StartupEventDetail({ initialEventData }) {
  const router = useRouter();
  const { id } = router.query;
  const [event, setEvent] = useState(initialEventData);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState({
    publish: false,
    approve: false
  });

  const fetchEventData = async () => {
    try {
      const response = await getStartupEventById(id);
      setEvent(response.data.data);
    } catch (error) {
      console.error('Error fetching event data:', error);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteStartupEvent(id);
      router.push('/startup-events');
    } catch (error) {
      console.error('Error deleting event:', error);
    }
  };

  const handlePublishToggle = async () => {
    setIsLoading(prev => ({ ...prev, publish: true }));
    try {
      if (event.is_published) {
        await unPublishStartupEvent(id);
        toast.success('Event successfully unpublished');
      } else {
        await publishStartupEvent(id);
        toast.success('Your event has been submitted for review');
      }
      await fetchEventData();
    } catch (error) {
      console.error('Error toggling publish state:', error);
      toast.error('Failed to update event publish status');
    } finally {
      setIsLoading(prev => ({ ...prev, publish: false }));
    }
  };

  const handleApprove = async () => {
    setIsLoading(prev => ({ ...prev, approve: true }));
    try {
      await approveStartupEvent(id);
      toast.success('Event successfully approved');
      await fetchEventData();
    } catch (error) {
      console.error('Error approving event:', error);
      toast.error('Failed to approve event');
    } finally {
      setIsLoading(prev => ({ ...prev, approve: false }));
    }
  };

  const isAuthorized = () => {
    if (!user) return false;
    if (user.role === 'admin-team') return true;
    return user.id === event?.user_id;
  };

  if (!event) {
    return <Layout title="Loading..."><div className="flex justify-center items-center h-screen">Loading...</div></Layout>;
  }

  if (!event.is_published && !event.is_approved && !isAuthorized()) {
    return <NotFoundComponent />;
  }

  const eventDate = event.date ? new Date(event.date) : null;
  const formattedDate = eventDate && !isNaN(eventDate) ? format(eventDate, 'PPP') : 'Date not available';

  return (
    <Layout
      title={event.name}
      description={event.short_description}
    >
      <div className="relative">
        {event.is_published && !event.is_approved && (
          <div className="bg-yellow-50 border-b border-yellow-200 p-4">
            <div className="container mx-auto">
              <p className="text-yellow-800 text-center">
                Your event has been submitted for review. The review process may take up to 24 hours. 
                Once approved, your event will be publicly visible.
              </p>
            </div>
          </div>
        )}
        {!event.is_published && user && event.user_id === user.id && (
          <div className="bg-gray-50 border-b border-gray-200 p-4">
            <div className="container mx-auto">
              <p className="text-gray-800 text-center flex items-center justify-center gap-2">
                <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-sm font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10">
                  Draft
                </span>
                This event is currently unpublished and is only visible to you
              </p>
            </div>
          </div>
        )}
        <div className="hero-section relative h-96">
          <Image
            src={event.image_url || event.background_image_url || DummyLogo}
            alt="Event Image"
            layout="fill"
            objectFit="cover"
            className="absolute inset-0 z-0"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-black/30 flex items-center justify-center z-10">
            <div className="text-center text-white">
              <h1 className="text-5xl font-bold mb-4">{event.name}</h1>
              <p className="text-xl">{formattedDate}</p>
            </div>
          </div>
        </div>
        <div className="relative z-10 container mx-auto -mt-20 px-4 pb-12">
          <Card className="shadow-xl">
            <CardContent className="p-8">
              <div className="mb-6">
                <Link href="/startup-events">
                  <Button variant="outline" className="mb-4">
                    Back to Events
                  </Button>
                </Link>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="md:col-span-2 space-y-6">
                  <h2 className="text-2xl font-semibold mb-4">About the Event</h2>
                  <p className="text-gray-500">{event.short_description}</p>
                  
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <Calendar className="h-6 w-6 text-gray-500 mr-2" />
                      <span>{formattedDate}</span>
                    </div>
                    <div className="flex items-center">
                      <MapPin className="h-6 w-6 text-gray-500 mr-2" />
                      <span>{`${event.location}, ${event.city}, ${event.country}`}</span>
                    </div>
                    <div className="flex items-center">
                      <User className="h-6 w-6 text-gray-500 mr-2" />
                      <span>{event.organizer}</span>
                    </div>
                    {event.website && <div className="flex items-center">
                      <Globe className="h-6 w-6 text-gray-500 mr-2" />
                      <a href={event.website} target="_blank" rel="noopener noreferrer" className="text-orange-400 hover:underline">{event.website}</a>
                    </div>}
                  </div>
                </div>
                <div>
                  <div className="sticky top-4">
                    <div className="mb-6 rounded-lg overflow-hidden" style={{ height: '300px' }}>
                      {typeof window !== 'undefined' && (
                        <Map center={[event.lat, event.lng]} zoom={13} />
                      )}
                    </div>
                    <div className='mb-10'>
                      {user?.roles?.includes('admin-team') && !event.is_approved && (
                        <Button
                          className="w-full flex items-center justify-center bg-orange-600 hover:bg-orange-700 text-white"
                          onClick={handleApprove}
                          disabled={isLoading.approve}
                        >
                          {isLoading.approve ? (
                            <span className="animate-spin mr-2">⭮</span>
                          ) : (
                            <User className="w-4 h-4 mr-2" />
                          )}
                          {isLoading.approve ? 'Approving...' : 'Approve Event'}
                        </Button>
                      )}
                    </div>
                    <div className="flex flex-col space-y-3">
                      {(user && event.user_id === user.id) && (
                        <>
                          <Button
                            className={`w-full flex items-center justify-center ${
                              event.is_published ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-green-600 hover:bg-green-700'
                            } text-white`}
                            onClick={handlePublishToggle}
                            disabled={isLoading.publish}
                          >
                            {isLoading.publish ? (
                              <span className="animate-spin mr-2">⭮</span>
                            ) : (
                              <Globe className="w-4 h-4 mr-2" />
                            )}
                            {isLoading.publish 
                              ? (event.is_published ? 'Unpublishing...' : 'Publishing...') 
                              : (event.is_published ? 'Unpublish Event' : 'Publish Event')
                            }
                          </Button>
                          <Link href={`/startup-events/${id}/edit`}>
                            <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center">
                              <Pencil className="w-4 h-4 mr-2" />
                              Edit Event
                            </Button>
                          </Link>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {(user && event.user_id === user.id) && (
            <div className="mt-12 mb-8">
              <div className="max-w-md mx-auto">
                <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
                  <h3 className="text-red-800 font-semibold text-lg mb-4 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    Danger Zone
                  </h3>
                  <p className="text-red-700 text-sm mb-4">
                    Once you delete this event, there is no going back. Please be certain.
                  </p>
                  <Button 
                    variant="destructive" 
                    className="w-full flex items-center justify-center !bg-red-600 hover:!bg-red-700"
                    onClick={() => setIsDeleteModalOpen(true)}
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete Event
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <ModalConfirm
        isOpen={isDeleteModalOpen}
        setIsOpen={setIsDeleteModalOpen}
        title="Delete Event"
        description="Are you sure you want to delete this event? This action cannot be undone."
        onSubmit={handleDelete}
        onCancel={() => setIsDeleteModalOpen(false)}
      />
    </Layout>
  );
}

export async function getServerSideProps({ params }) {
  try {
    const response = await getStartupEventById(params.id);
    return {
      props: {
        initialEventData: response.data.data,
      },
    };
  } catch (error) {
    console.error('Error fetching event data:', error);
    return {
      notFound: true,
    };
  }
}
